import { Box, Typography } from '@mui/material'
import type { EChartsOption } from 'echarts'
import ReactECharts from 'echarts-for-react'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getMarketProgramColor } from '@/features/activationPlan/utils/marketProgramColors'
import { transformScheduleToChartData } from '@/features/activationPlan/utils/volumeTransformations'
import type { RegulationObjectSchedule } from '@/openapi/v1/msa-portfolio-manager'

type Props = Readonly<{
  schedule: RegulationObjectSchedule
}>

function ActivationPlanChart({ schedule }: Props) {
  const { t } = useTranslation()

  const chartData = transformScheduleToChartData(schedule)

  const option: EChartsOption = useMemo(() => {
    const maxAbsValue = Math.max(...chartData.series.flatMap((s) => s.data.map((d) => Math.abs(d.value))), 0)
    const yAxisMax = Math.ceil(maxAbsValue * 1.1)

    // Use custom series to render bars with proper time-based widths
    const series = chartData.series.map((s) => ({
      name: s.name,
      type: 'custom' as const,
      renderItem: (params: any, api: any) => {
        const dataIndex = params.dataIndex
        const dataPoint = s.data[dataIndex]

        const startPos = api.coord([dataPoint.startTime, 0])
        const endPos = api.coord([dataPoint.endTime, 0])
        const width = endPos[0] - startPos[0]

        if (s.stack === 'symmetric') {
          const topPos = api.coord([dataPoint.startTime, dataPoint.value])
          const bottomPos = api.coord([dataPoint.startTime, -dataPoint.value])
          const height = bottomPos[1] - topPos[1]

          return {
            type: 'rect' as const,
            shape: {
              x: startPos[0],
              y: topPos[1],
              width,
              height,
            },
            style: {
              fill: getMarketProgramColor(s.marketProgram),
            },
          }
        } else {
          const valuePos = api.coord([dataPoint.startTime, dataPoint.value])
          const zeroPos = api.coord([dataPoint.startTime, 0])
          const height = zeroPos[1] - valuePos[1]

          return {
            type: 'rect' as const,
            shape: {
              x: startPos[0],
              y: valuePos[1],
              width,
              height,
            },
            style: {
              fill: getMarketProgramColor(s.marketProgram),
            },
          }
        }
      },
      data: s.data.map((d) => [d.startTime, d.value]),
      stack: s.stack,
      itemStyle: {
        color: getMarketProgramColor(s.marketProgram),
      },
    }))

    return {
      tooltip: {
        trigger: 'item' as const,
        confine: true,
        formatter: (params: any) => {
          if (!params?.data) return ''

          const dataIndex = params.dataIndex
          const series = chartData.series.find((s) => s.name === params.seriesName)
          if (!series) return ''

          const dataPoint = series.data[dataIndex]
          const startDt = DateTime.fromMillis(dataPoint.startTime)
          const endDt = DateTime.fromMillis(dataPoint.endTime)
          const timeRange = `${startDt.toFormat('HH:mm')} - ${endDt.toFormat('HH:mm')}`
          const displayValue = series.stack === 'symmetric' ? dataPoint.value * 2 : Math.abs(dataPoint.value)
          const value = displayValue.toFixed(2)

          return `<div style="padding: 4px 8px;"><strong>${timeRange}</strong><br/>${params.marker} ${params.seriesName}: <strong>${value} MW</strong></div>`
        },
      },
      legend: {
        data: chartData.series.map((s) => s.name),
        bottom: 0,
        type: 'scroll',
      },
      grid: {
        left: '5%',
        right: '5%',
        top: '10%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          rotate: 45,
          formatter: (value: number) => {
            return DateTime.fromMillis(value).toFormat('HH:mm')
          },
        },
      },
      yAxis: {
        type: 'value',
        name: 'Capacity (MW)',
        min: -yAxisMax,
        max: yAxisMax,
        axisLabel: {
          formatter: '{value}',
        },
        splitLine: {
          lineStyle: {
            color: '#e0e0e0',
          },
        },
      },
      series,
    }
  }, [chartData.series])

  if (chartData.series.length === 0) {
    return (
      <Typography color="text.secondary" variant="body1">
        {t('activation_plan.no_data')}
      </Typography>
    )
  }

  const totalPtus = chartData.series.reduce((sum, s) => Math.max(sum, s.data.length), 0)

  return (
    <Box>
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        {chartData.series.length} market program(s), {totalPtus} PTU(s)
      </Typography>
      <ReactECharts
        data-testid="activationPlan-chart"
        lazyUpdate={true}
        notMerge={true}
        option={option}
        style={{ height: 500 }}
      />
    </Box>
  )
}

export default ActivationPlanChart
